import { useEffect, useRef, useState } from 'react';
import { FileText, ExternalLink, Lightbulb, ShieldCheck, Database, Globe, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import type { AiDocSource, AiDataSection, AiWebSource } from '../../types/aiSources';

interface SourcesPanelProps {
    sources: AiDocSource[];
    reasoning: string | null;
    /** Source id last clicked in the chat via an inline [n] marker. */
    highlightedId: number | null;
    isSending: boolean;
    /** Context sections the answer was built from. Omitted by the SDS assistant. */
    dataSections?: AiDataSection[];
    /** Pages returned by the web_search tool. Only present on the AI gateway. */
    webSources?: AiWebSource[];
    /** The answer was cut short — shown as a chip so it is never a silent truncation. */
    truncated?: boolean;
    /** Replaces the default "nothing yet" copy. */
    emptyState?: React.ReactNode;
    /**
     * Which of `sources` the answer actually cites. Retrieval is question-driven, so a
     * question about revenue still pulls five datasheets — listing them unqualified
     * reads as "your revenue figure comes from a supplier score card". Everything stays
     * in the list (it is what the model was given), but what it leaned on is marked.
     * Omit to label nothing, which is what the SDS assistant wants: there, every
     * question is a datasheet question.
     */
    citedIds?: number[];
}

/** Same badge colours as the Customer-360 datasheet list, so doc types read the same everywhere. */
function docTypeBadge(docType: string | null): string {
    if (docType === '2_TDS') return 'bg-blue-50 text-blue-600';
    if (docType === '1_SDS') return 'bg-orange-50 text-orange-600';
    if (docType === '3_REGULATORY') return 'bg-purple-50 text-purple-600';
    return 'bg-gray-50 text-gray-600';
}

export function SourcesPanel({
    sources, reasoning, highlightedId, isSending,
    dataSections, webSources, truncated, emptyState, citedIds,
}: SourcesPanelProps) {
    const refs = useRef<Record<number, HTMLDivElement | null>>({});
    const [dataOpen, setDataOpen] = useState(false);

    useEffect(() => {
        if (highlightedId === null) return;
        refs.current[highlightedId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [highlightedId]);

    const hasAnything = sources.length > 0
        || (dataSections?.length ?? 0) > 0
        || (webSources?.length ?? 0) > 0
        || !!reasoning;

    return (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-gray-700">Sources &amp; reasoning</span>
                {sources.length > 0 && (
                    <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {sources.length}
                    </span>
                )}
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
                {!hasAnything && !isSending && (
                    <div className="flex h-full flex-col items-center justify-center px-2 text-center">
                        <FileText className="mb-3 h-8 w-8 text-gray-300" />
                        {emptyState ?? (
                            <>
                                <p className="text-xs text-gray-500">
                                    After each answer, the datasheets the answer was built from appear here —
                                    with the matching passage, relevance and a link to the original PDF.
                                </p>
                                <p className="mt-2 text-[11px] text-gray-400">
                                    Expired safety data sheets (4_OLD) are not considered.
                                </p>
                            </>
                        )}
                    </div>
                )}

                {isSending && !hasAnything && (
                    <div className="space-y-2">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="h-20 animate-pulse rounded-md bg-gray-100" />
                        ))}
                    </div>
                )}

                {truncated && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                        <p className="text-[11px] leading-relaxed text-amber-900">
                            This answer was cut short before it finished. Ask a narrower question, or ask for
                            the missing part directly.
                        </p>
                    </div>
                )}

                {sources.map(source => {
                    const isHighlighted = source.id === highlightedId;
                    const uncited = citedIds !== undefined && !citedIds.includes(source.id);
                    return (
                        <div
                            key={source.id}
                            ref={el => { refs.current[source.id] = el; }}
                            className={`rounded-md border p-3 transition-colors ${
                                isHighlighted ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-white'
                            } ${uncited && !isHighlighted ? 'opacity-60' : ''}`}
                        >
                            <div className="flex items-start gap-2">
                                <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[11px] font-semibold ${
                                    isHighlighted ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'
                                }`}>
                                    {source.id}
                                </span>
                                <div className="min-w-0 flex-1">
                                    {source.webUrl ? (
                                        <a
                                            href={source.webUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-start gap-1 text-xs font-medium text-blue-600 hover:underline"
                                        >
                                            <span className="break-words">{source.fileName}</span>
                                            <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                        </a>
                                    ) : (
                                        <span className="text-xs font-medium text-gray-700">{source.fileName}</span>
                                    )}

                                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-400">
                                        {uncited && (
                                            <span
                                                className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500"
                                                title="Retrieved for this question, but the answer does not cite it"
                                            >
                                                not cited
                                            </span>
                                        )}
                                        {source.docType && (
                                            <span className={`rounded px-1.5 py-0.5 ${docTypeBadge(source.docType)}`}>
                                                {source.docType}
                                            </span>
                                        )}
                                        {source.supplierFolder && <span>{source.supplierFolder}</span>}
                                        {source.score !== null && (
                                            <span title="Semantic relevance score (0–4)">
                                                Relevance {source.score.toFixed(2)}
                                            </span>
                                        )}
                                    </div>

                                    {source.caption && (
                                        <blockquote className="mt-2 border-l-2 border-gray-200 pl-2 text-[11px] italic leading-relaxed text-gray-600">
                                            «{source.caption}»
                                        </blockquote>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Data used — named, not numbered. These are the always-there context blocks. */}
                {dataSections && dataSections.length > 0 && (
                    <div className="rounded-md border border-gray-200 bg-gray-50">
                        <button
                            type="button"
                            onClick={() => setDataOpen(v => !v)}
                            className="flex w-full items-center gap-1.5 px-3 py-2 text-left"
                        >
                            <Database className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                            <span className="text-xs font-medium text-gray-700">
                                Data used ({dataSections.length})
                            </span>
                            {dataOpen
                                ? <ChevronDown className="ml-auto h-3.5 w-3.5 text-gray-500" />
                                : <ChevronRight className="ml-auto h-3.5 w-3.5 text-gray-500" />
                            }
                        </button>
                        {dataOpen && (
                            <ul className="space-y-1 px-3 pb-2">
                                {dataSections.map((section, i) => (
                                    <li
                                        key={`${section.title}-${i}`}
                                        title={section.detail}
                                        className="border-l-2 border-gray-300 pl-2 text-[11px] leading-relaxed text-gray-600"
                                    >
                                        <span className="block break-words">{section.title}</span>
                                        <span className="block break-words text-gray-400">{section.detail}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {webSources && webSources.length > 0 && (
                    <div className="rounded-md border border-gray-200 bg-white p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                            <Globe className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-xs font-medium text-gray-700">
                                Web sources ({webSources.length})
                            </span>
                        </div>
                        <ul className="space-y-1">
                            {webSources.map(web => (
                                <li key={web.url}>
                                    <a
                                        href={web.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start gap-1 text-[11px] text-blue-600 hover:underline"
                                    >
                                        <span className="break-all">{web.title || web.url}</span>
                                        <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {reasoning && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                        <div className="mb-1.5 flex items-center gap-1.5">
                            <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-xs font-medium text-amber-900">
                                How the answer was derived
                            </span>
                        </div>
                        <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-amber-900/80">
                            {reasoning}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
